import React, { ReactElement, useState, useCallback } from "react";
import classNames from "classnames";
import { SidebarMenuProps } from "./SidebarMenu.types";
import { CaptionBolder, SmallBodyBolder } from "src/components/Text";
import {
    SidebarMenuGroup,
    SidebarMenuItem,
} from "../types";
import { ConditionalRender } from "src/components/ConditionalRender";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Conditional } from "src/components/Conditional";
import { Link } from "react-router-dom";
import { UnstyledButton } from "src/ui";
import { useSelector } from "react-redux";


function renderSidebarItem(item: SidebarMenuItem): ReactElement {
    const className = classNames("SidebarMenu-item", {
        "SidebarMenu-item--active": item.active,
        "SidebarMenu-item--disabled": item.disabled,
    });

    const iconColor: spintrColors = item.active
        ? "light-blue"
        : "grey";

    return (
        <li key={item.key} className={className}>
            <Link className="SidebarMenu-link"
                  to={item.href}
                  onClick={item.onClick}>
                <ConditionalRender condition={!!item.icon}>
                    {() => (
                        <Visage2Icon className="SidebarMenu-itemIcon"
                                     color={iconColor}
                                     icon={item.icon} />
                    )}
                </ConditionalRender>
                <SmallBodyBolder as="span"
                                 className="SidebarMenu-itemLabel"
                                 color={item.active ? "contentDark" : "contentLight"}>
                    {item.label}
                </SmallBodyBolder>
                <Conditional condition={item.active}>
                    <span className="SidebarMenu-itemActive">
                        <Visage2Icon color="light-blue"
                                     icon="arrow-right-3"
                                     size="small" />
                    </span>
                </Conditional>
            </Link>
        </li>
    );
}

function renderSidebarGroup(group: SidebarMenuGroup): ReactElement {
    return (
        <li className="SidebarMenu-group"
            key={group.key}>
            <CaptionBolder as="div"
                           className="SidebarMenu-groupLabel"
                           color="contentLight">
                {group.label}
            </CaptionBolder>
            <ol className="SidebarMenu-itemList">
                {group.items.map(renderSidebarItem)}
            </ol>
        </li>
    );
}

function SidebarMenu({
    collapsible,
    groups,
    title,
}: SidebarMenuProps): ReactElement {
    const smallViewMode = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.ui.isSmallViewMode,
    );

    const [isCollapsed, setIsCollapsed] = useState(smallViewMode);
    const className = classNames("SidebarMenu", {
        "SidebarMenu--collapsed": isCollapsed,
    });

    const onCollapseClick = useCallback(
        () => setIsCollapsed((prevState) => !prevState),
        [setIsCollapsed]);

    return (
        <section className={className}>
            <Conditional condition={title || collapsible}>
                <div className="SidebarMenu-header">
                    <Conditional condition={collapsible}>
                        <UnstyledButton className="SidebarMenu-collapseButton"
                                        onClick={onCollapseClick}>
                            <Visage2Icon className="SidebarMenu-collapse"
                                         color="spintrGrey"
                                         icon="menu-1"
                                         size="medium" />
                        </UnstyledButton>
                    </Conditional>
                    <Conditional condition={title}>
                        <SmallBodyBolder className="SidebarMenu-title"
                                         color="contentNormal">
                            {title}
                        </SmallBodyBolder>
                    </Conditional>
                </div>
            </Conditional>
            <nav className="SidebarMenu-groups">
                <ol className="SidebarMenu-groupList">
                    {groups.map(renderSidebarGroup)}
                </ol>
            </nav>
        </section>
    );
}

export default SidebarMenu;

